class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this._priceSyncTimers = [];
    this._schedulePriceSync = () => {
      this._priceSyncTimers.forEach((id) => clearTimeout(id));
      this._priceSyncTimers = [];

      const scope = this.getProductPriceScope?.() || document;
      const delays = this.hasDmixerDiscountedPrice?.(scope) ? [350, 900, 1600] : [750];

      delays.forEach((delay) => {
        const id = setTimeout(() => this.updatePrices(), delay);
        this._priceSyncTimers.push(id);
      });
    };

    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        this._schedulePriceSync();
      });
    }

    document.addEventListener('evamats:discount-updated', () => {
      this._schedulePriceSync();
    });

    window.addEventListener('load', () => {
      this._schedulePriceSync();
    });
  }

  getProductPriceScope() {
    return (
      this.closest('.product__info-wrapper') ||
      this.closest('.product') ||
      this.closest('[id^="shopify-section"]') ||
      document
    );
  }

  formatMoneyLikeDisplay(cents, referenceHTML) {
    const currency = (referenceHTML || '').replace(/[\d.,\s]/g, '').trim() || 'zł';
    const amount = Number(cents) / 100;
    if (!amount) return '';
    return `${Math.round(amount).toLocaleString('pl-PL')} ${currency}`.trim();
  }

  findDmixerCompareElement(host, shadowRoot, wrapper, saleText) {
    const saleNormalized = (saleText || '').replace(/\s+/g, ' ').trim();

    const shadowSelectors = [
      '.dmixer-dp-original-price',
      '.dmixer-dp-compare',
      '.dmixer-dp-original',
      '[class*="original"]',
      's',
      'del',
    ];
    for (const selector of shadowSelectors) {
      const el = shadowRoot.querySelector(selector);
      if (el && el.textContent.trim() && el.textContent.replace(/\s+/g, ' ').trim() !== saleNormalized) {
        return el;
      }
    }

    for (const el of shadowRoot.querySelectorAll('*')) {
      if (!el.textContent.trim() || el === shadowRoot.querySelector('.dmixer-dp-price')) continue;
      const decoration = window.getComputedStyle(el).textDecorationLine;
      if (decoration.includes('line-through') && el.textContent.replace(/\s+/g, ' ').trim() !== saleNormalized) {
        return el;
      }
    }

    if (!wrapper) return null;

    const lightSelectors = [
      '.dmixer-original-price',
      '.dmixer-dp-original-price',
      '[class*="original"]',
      's',
      'del',
      '.price-item--regular',
    ];
    for (const selector of lightSelectors) {
      const el = wrapper.querySelector(selector);
      if (el && !el.closest('dmixer-discounted-price') && el.textContent.trim()) {
        const text = el.textContent.replace(/\s+/g, ' ').trim();
        if (text !== saleNormalized) return el;
      }
    }

    const priceBox = host.closest('.dmixer-price') || wrapper.querySelector('.dmixer-price');
    if (priceBox) {
      for (const child of priceBox.children) {
        if (child.tagName === 'DMIXER-DISCOUNTED-PRICE' || child.contains(host)) continue;
        const text = child.textContent.replace(/\s+/g, ' ').trim();
        if (text && text !== saleNormalized) return child;
      }
    }

    return null;
  }

  getDmixerPricesFromShadow(scope) {
    const hosts = scope.querySelectorAll('dmixer-discounted-price');
    for (const host of hosts) {
      const shadowRoot = host.shadowRoot;
      if (!shadowRoot) continue;

      const saleEl = shadowRoot.querySelector('.dmixer-dp-price');
      if (!saleEl || !saleEl.textContent.trim()) continue;

      const saleHTML = saleEl.innerHTML.trim() || saleEl.textContent.trim();
      const wrapper =
        host.closest('.dmixer-price-wrapper, .dmixer-price') ||
        scope.querySelector('.dmixer-price-wrapper, .dmixer-price, #google_prices, .main_price');

      const compareEl = this.findDmixerCompareElement(host, shadowRoot, wrapper, saleHTML);
      let compareHTML =
        compareEl && compareEl.textContent.trim()
          ? compareEl.innerHTML.trim() || compareEl.textContent.trim()
          : null;

      if (!compareHTML && this.currentVariant?.price) {
        compareHTML = this.formatMoneyLikeDisplay(this.currentVariant.price, saleHTML);
      }

      return { saleHTML, compareHTML };
    }

    return null;
  }

  hasDmixerDiscountedPrice(scope) {
    return scope.querySelector('dmixer-discounted-price') !== null;
  }

  getDisplayedProductPrices() {
    const scope = this.getProductPriceScope();

    const dmixerPrices = this.getDmixerPricesFromShadow(scope);
    if (dmixerPrices) {
      return dmixerPrices;
    }

    const evamatsRoot = scope.querySelector('[data-evamats-discount]:not([hidden])');
    if (evamatsRoot) {
      const saleEl = evamatsRoot.querySelector('[data-evamats-sale]');
      const compareEl = evamatsRoot.querySelector('[data-evamats-compare]');
      if (saleEl && saleEl.textContent.trim()) {
        return {
          saleHTML: saleEl.innerHTML,
          compareHTML: compareEl && compareEl.textContent.trim() ? compareEl.innerHTML : null,
        };
      }
    }

    const mainPrice = scope.querySelector('.main_price') || scope.querySelector('#google_prices');
    if (!mainPrice) {
      return { saleHTML: '', compareHTML: null };
    }

    const mixerPrice = mainPrice.querySelector('.dmixer-price .price-item');
    const mixerCompare = mainPrice.querySelector('.dmixer-original-price');
    if (mixerPrice && mixerPrice.textContent.trim()) {
      return {
        saleHTML: mixerPrice.innerHTML,
        compareHTML: mixerCompare && mixerCompare.textContent.trim() ? mixerCompare.innerHTML : null,
      };
    }

    const priceRoot = mainPrice.querySelector('.price') || mainPrice;
    const onSale = priceRoot.classList.contains('price--on-sale');
    const saleEl = onSale
      ? priceRoot.querySelector('.price-item--sale, .price-item--last')
      : priceRoot.querySelector('.price__regular .price-item--regular, .price-item--regular');
    const compareEl = onSale
      ? priceRoot.querySelector('.price__sale s.price-item--regular, .price__sale .price-item--regular')
      : null;

    return {
      saleHTML: saleEl ? saleEl.innerHTML : '',
      compareHTML: compareEl && compareEl.textContent.trim() ? compareEl.innerHTML : null,
    };
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    // this.toggleAddButton(true, '', false);
    // this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      // this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      if( !this.closest('.config_container')) {
        this.updateMedia();
      }
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      // this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    mediaGalleries.forEach(mediaGallery => mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true));

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updatePrices() {
    const stickyConfigPriceElement = document.querySelector('.sticky_config_price');
    const stickyConfigPriceElementPrice = document.querySelector('.sticky_config_price_price');
    const priceElement1 = document.querySelector('.product__message_price_1');
    const priceElement2 = document.querySelector('.product__message_price_2');
    const buttonsPrice = document.querySelectorAll('.button_price');
    const upsellItemsCheckboxes = document.querySelectorAll('.product__upsell_custom_checkbox input[type="checkbox"]:checked');

    let upsellTotal = 0;
    upsellItemsCheckboxes.forEach(checkbox => {
      upsellTotal += parseFloat(checkbox.dataset.price) / 100 || 0;
    });

    const { saleHTML, compareHTML } = this.getDisplayedProductPrices();
    if (!saleHTML) return;

    const parsePriceFromHTML = (htmlString) => {
      const raw = (htmlString || '').replace(/[^\d.,\s]/g, '').trim();
      if (!raw) return 0;
      if (/,/.test(raw)) {
        return parseFloat(raw.replace(/[\s.]/g, '').replace(',', '.')) || 0;
      }
      const parts = raw.split('.');
      if (parts.length === 2 && parts[1].length === 3) {
        return parseFloat(parts[0] + parts[1]) || 0;
      }
      return parseFloat(raw.replace(/\s/g, '')) || 0;
    };

    const getSumHTML = (htmlString, addValue) => {
      const numericValue = parsePriceFromHTML(htmlString);
      const currency = (htmlString || '').replace(/[\d.,\s]/g, '').trim() || 'zł';
      const sum = numericValue + addValue;
      return `${Math.round(sum).toLocaleString('pl-PL')} ${currency}`.trim();
    };

    const totalHTML = getSumHTML(saleHTML, upsellTotal);
    const buttonPrice = document.querySelectorAll('.product__info-wrapper .button_price');
    const optionPrice = document.querySelector('.option__price_regular');
    const optionPriceCompare = document.querySelector('.option__price_compare');

    if (stickyConfigPriceElementPrice) {
      stickyConfigPriceElementPrice.innerHTML = totalHTML;
    }
    buttonPrice.forEach((button) => {
      button.innerHTML = totalHTML;
    });
    buttonsPrice.forEach((button) => {
      button.innerHTML = totalHTML;
    });

    if (optionPrice) {
      optionPrice.innerHTML = saleHTML;
    }
    if (optionPriceCompare) {
      let fullPriceHTML = compareHTML;

      if (!fullPriceHTML && this.currentVariant?.price) {
        const saleNum = parsePriceFromHTML(saleHTML);
        const variantNum = this.currentVariant.price / 100;
        if (variantNum > saleNum) {
          fullPriceHTML = this.formatMoneyLikeDisplay(this.currentVariant.price, saleHTML);
        }
      }

      if (fullPriceHTML) {
        optionPriceCompare.classList.remove('hidden');
        optionPriceCompare.innerHTML = fullPriceHTML;
        optionPrice?.classList.add('option__price_regular--sale');
      } else {
        optionPriceCompare.classList.add('hidden');
        optionPriceCompare.innerHTML = '';
        optionPrice?.classList.remove('option__price_regular--sale');
      }
    }

    if (stickyConfigPriceElement) {
      stickyConfigPriceElement.removeAttribute('style');
      stickyConfigPriceElement.style.width = stickyConfigPriceElement.offsetWidth + 1 + 'px';
    }

    if (priceElement1) priceElement1.innerHTML = saleHTML;
    if (priceElement2) priceElement2.innerHTML = compareHTML || saleHTML;
  }


  updateVariantInput() {
    if (!this.currentVariant) return;
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input =
        productForm.querySelector('input.product-variant-id[name="id"]') ||
        productForm.querySelector('input[name="id"]');
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const firstChecked = this.querySelector(':checked');
    if (!firstChecked || !this.variantData) return;
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => firstChecked.value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')]
      const prevChecked = inputWrappers[index - 1].querySelector(':checked');
      if (!prevChecked) return;
      const previousOptionSelected = prevChecked.value;
      const availableOptionInputsValue = selectedOptionOneVariants.filter(variant => variant.available && variant[`option${index}`] === previousOptionSelected).map(variantOption => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue)
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  // updatePickupAvailability() {
  //   const pickUpAvailability = document.querySelector('pickup-availability');
  //   if (!pickUpAvailability) return;

  //   if (this.currentVariant && this.currentVariant.available) {
  //     pickUpAvailability.fetchAvailability(this.currentVariant.id);
  //   } else {
  //     pickUpAvailability.removeAttribute('available');
  //     pickUpAvailability.innerHTML = '';
  //   }
  // }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const skuSource = html.getElementById(`Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(`Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('visibility-hidden');

        if (inventoryDestination) inventoryDestination.classList.toggle('visibility-hidden', inventorySource.innerText === '');

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant
          }
        });
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
    if (inventory) inventory.classList.add('visibility-hidden');
    if (sku) sku.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
    this._onLabelCaptureClick = this._onLabelCaptureClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onLabelCaptureClick, true);
    // EVAMATS: on load, if the initially-checked combination is unavailable
    // (e.g. a ?variant= deep-link to a sold-out variant), snap the picker to an
    // available variant. Non-config products only (see _healInitialSelectionIfNeeded).
    requestAnimationFrame(() => this._healInitialSelectionIfNeeded());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onLabelCaptureClick, true);
  }

  /**
   * Some layouts (hidden native radios, stacking, browsers) skip toggling the
   * radio when the label is clicked; the UI looks selected but the form still
   * submits the previous variant. Force-check after the browser's default path.
   */
  _onLabelCaptureClick(event) {
    const label = event.target.closest('label[for]');
    if (!label || !this.contains(label)) return;
    const forId = label.getAttribute('for');
    if (!forId) return;
    const input = document.getElementById(forId);
    if (!input || input.type !== 'radio' || !this.contains(input) || input.disabled) return;

    const ensure = () => {
      if (input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(ensure);
    } else {
      setTimeout(ensure, 0);
    }
  }

  applyVariantToRadioInputs(variant) {
    if (!variant || !variant.options || !variant.options.length) return;
    this.querySelectorAll('fieldset[data-option-position]').forEach((fieldset) => {
      const pos = parseInt(fieldset.dataset.optionPosition, 10);
      if (!Number.isFinite(pos) || pos < 1 || pos > variant.options.length) return;
      const target = variant.options[pos - 1];
      if (target == null) return;
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      let needsUpdate = false;
      radios.forEach((radio) => {
        const shouldCheck = radio.value === target;
        if (shouldCheck !== radio.checked) needsUpdate = true;
      });
      if (!needsUpdate) return;
      radios.forEach((radio) => {
        radio.checked = radio.value === target;
      });
    });
  }

  updateVariantInput() {
    super.updateVariantInput();
    if (this.currentVariant) {
      this.applyVariantToRadioInputs(this.currentVariant);
    }
  }

  /**
   * Config products may omit some options from the DOM (e.g. Montaż) while Shopify
   * variants still include them in options[0]. Index-only matching then fails, so we
   * map each fieldset by data-option-position and fill gaps from the variant
   * currently selected in input[name="id"].
   */
  updateMasterId() {
    const variantData = this.getVariantData();
    if (!variantData.length) {
      this.currentVariant = null;
      return;
    }
    const numOpts = variantData[0].options.length;
    const productForm = document.querySelector(`#product-form-${this.dataset.section}`);
    const idInput = productForm
      ? productForm.querySelector('input.product-variant-id[name="id"]') ||
        productForm.querySelector('input[name="id"]')
      : null;
    const prevVariant = idInput
      ? variantData.find((v) => String(v.id) === String(idInput.value))
      : null;

    const fieldsets = Array.from(this.querySelectorAll('fieldset'));

    const buildSelected = (fillGapsFromPrev) => {
      const sel = new Array(numOpts).fill(undefined);
      fieldsets.forEach((fieldset) => {
        const pos = parseInt(fieldset.dataset.optionPosition, 10);
        if (!Number.isFinite(pos) || pos < 1 || pos > numOpts) return;
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) sel[pos - 1] = checked.value;
      });
      if (fillGapsFromPrev) {
        for (let i = 0; i < numOpts; i++) {
          if (sel[i] === undefined && prevVariant && prevVariant.options[i] !== undefined) {
            sel[i] = prevVariant.options[i];
          }
        }
      }
      return sel;
    };

    const match = (sel) =>
      variantData.filter((variant) =>
        variant.options.every((opt, i) => sel[i] === undefined || sel[i] === opt)
      );

    let candidates = match(buildSelected(true));
    if (!candidates.length) {
      candidates = match(buildSelected(false));
    }
    let chosen = candidates.find((v) => v.available) || candidates[0] || null;

    // EVAMATS auto-heal: never leave the picker on a non-existent or sold-out combination.
    // Snap to an available variant that keeps the option the customer just changed.
    // - Accessories / other non-config templates: always heal (allowSoldoutSelect absent).
    // - Default template (allowSoldoutSelect="true"): heal ONLY when the FIRST option
    //   (position 1, e.g. "Zestaw pokrowców") was just changed, so switching the set always
    //   lands on an available variant; changing a later option (e.g. colour) may still land
    //   on a sold-out combination on purpose (strikethrough + "Wyprzedane" button).
    // - Config products are excluded on purpose.
    if (
      !this.closest('.config_container') &&
      (this.dataset.allowSoldoutSelect !== 'true' || this._lastChangedPosition === 1) &&
      (!chosen || !chosen.available)
    ) {
      const healed = this._healToAvailableVariant(this.getVariantData(), buildSelected(false));
      if (healed) chosen = healed;
    }

    this.currentVariant = chosen;
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      const checked = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
        (radio) => radio.checked
      );
      return checked ? checked.value : '';
    });
  }

  // EVAMATS: capture which option the customer just changed, so the auto-heal can
  // keep it fixed while snapping the other option(s) to an available variant.
  onVariantChange(event) {
    this._lastChangedPosition = this._changedOptionPosition(event);
    super.onVariantChange(event);
    this._applySoldoutButtonState();
  }

  // EVAMATS: default template only (data-allow-soldout-select). Auto-heal is skipped for
  // these pickers so the customer can actually land on a sold-out / non-existent option;
  // reflect that on the add button — show "Wyprzedane" and disable it while the current
  // selection isn't purchasable, restore "Dodaj do koszyka" once an available combo is set.
  // No-op for config / accessories (flag absent), so their behaviour is unchanged.
  _applySoldoutButtonState() {
    if (this.dataset.allowSoldoutSelect !== 'true') return;
    const purchasable = !!(this.currentVariant && this.currentVariant.available);
    if (purchasable) {
      this.toggleAddButton(false, '');
    } else {
      this.toggleAddButton(true, window.variantStrings.soldOut);
    }
  }

  _changedOptionPosition(event) {
    const fieldset =
      event && event.target && event.target.closest
        ? event.target.closest('fieldset[data-option-position]')
        : null;
    const pos = fieldset ? parseInt(fieldset.dataset.optionPosition, 10) : NaN;
    return Number.isFinite(pos) ? pos : null;
  }

  // Pick an available variant that preserves the just-changed option (falls back to any
  // available variant), ranking by how many of the other current selections it matches.
  _healToAvailableVariant(variantData, sel) {
    const available = variantData.filter((v) => v.available);
    if (!available.length) return null;
    const pos = this._lastChangedPosition;
    let pool = available;
    if (Number.isFinite(pos) && pos >= 1 && sel[pos - 1] != null) {
      const keep = sel[pos - 1];
      const kept = available.filter((v) => v.options[pos - 1] === keep);
      if (kept.length) pool = kept;
    }
    const score = (v) =>
      v.options.reduce((n, opt, i) => n + (sel[i] != null && sel[i] === opt ? 1 : 0), 0);
    return pool.slice().sort((a, b) => score(b) - score(a))[0] || null;
  }

  // On load: if the initially-checked combination is unavailable, snap to an available
  // variant and sync the UI. Non-config only; no-op when the initial selection is fine.
  _healInitialSelectionIfNeeded() {
    if (this.closest('.config_container')) return;
    const variantData = this.getVariantData();
    if (!variantData || !variantData.length) return;
    this._lastChangedPosition = null;
    this.updateOptions();
    this.updateMasterId();
    if (!this.currentVariant) return;
    const seld = this.options.map((v) => (v === '' ? null : v));
    const exact = variantData.find((v) =>
      v.options.every((opt, i) => seld[i] == null || seld[i] === opt)
    );
    if (exact && exact.available) return;
    this.applyVariantToRadioInputs(this.currentVariant);
    this.updateVariantStatuses();
    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    this.renderProductInfo();
  }
}

customElements.define('variant-radios', VariantRadios);

// reverse years in select on product
(function () {
  document.addEventListener("DOMContentLoaded", function() {
    const select = document.getElementById("rok-produkcji");
    if (select) {
        // Берём все option, кроме первого
        const firstOption = select.options[0];
        const options = Array.from(select.options).slice(1);

        // Реверсим список годов
        options.reverse();

        // Чистим select и возвращаем первый option "Please select"
        select.innerHTML = "";
        select.appendChild(firstOption);

        // Добавляем обратно перевёрнутые года
        options.forEach(option => select.appendChild(option));

        // Гарантируем, что выбран "Please select"
        firstOption.selected = true;
    }
  });
})();
  // reverse years in select on product


// swiper cart notification
(function () {
    if (!document.querySelector('.related_product__list')) return;
    function initRelatedSwiper() {
        if (typeof Swiper === 'undefined') return;
        new Swiper('.related_product__list', {
            spaceBetween: 10,
            slidesPerView: 2,
            navigation: {
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            },
            breakpoints: {
                800: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
            }
        });
    }
    window.ensureSwiperLoaded ? window.ensureSwiperLoaded(initRelatedSwiper) : initRelatedSwiper();
})();
// swiper cart notification

// toggle galleries on change mats type inputs
(function () {
    const radioButtons = document.querySelectorAll('product-info input[name="Rodzaj dywaników"]');
    if (radioButtons) {
      const mediaGalleryWithoutEdges = document.querySelector('.product__media_without_edges');
      const mediaGalleryWithEdges = document.querySelector('.product__media_with_edges');
  
      // const scrollToTop = () => {
      //   if (window.innerWidth <= 767) {
      //     window.scrollTo(0, 150);
      //   }
      // }
  
      const clickSecondImage = (value) => {
        if (value === "3D BEZ RANTÓW") {
          mediaGalleryWithoutEdges.querySelectorAll('.thumbnail')[1].click()
        }
        if (value === "3D Z RANTAMI") {
          mediaGalleryWithEdges.querySelectorAll('.thumbnail')[1].click()
        }
      }
  
      radioButtons.forEach((radio) => {
        if (radio.checked && radio.value === "3D BEZ RANTÓW") {
          mediaGalleryWithoutEdges.classList.add('active')
          mediaGalleryWithEdges.classList.remove('active')
        } else if (radio.checked && radio.value === "3D Z RANTAMI") {
          mediaGalleryWithoutEdges.classList.remove('active')
          mediaGalleryWithEdges.classList.add('active')
        }
        radio.addEventListener("change", function () {
          // scrollToTop()
          if (this.value === "3D BEZ RANTÓW") {
            mediaGalleryWithoutEdges.classList.add('active')
            mediaGalleryWithEdges.classList.remove('active')
            clickSecondImage(radio.value)
  
          } else if (this.value === "3D Z RANTAMI") {
            mediaGalleryWithoutEdges.classList.remove('active')
            mediaGalleryWithEdges.classList.add('active')
            clickSecondImage(radio.value)
          }
  
        });
      });
    }
  })();


// toggle size product config mobile
// (function () {
//   const toggler = document.querySelector('.product__config__toggle_size')
//   const container = document.querySelector('.sticky_config_price')
//   const image = document.querySelector('.product__config_image_image')
//   if (toggler) {
    
//     toggler.addEventListener('click', () => {
//       toggler.classList.toggle('active')
//       container.classList.toggle('wide')
//       container.removeAttribute('style')
//     })
//     image.addEventListener('click', () => {
//       toggler.classList.toggle('active')
//       container.classList.toggle('wide')
//       container.removeAttribute('style')
//     })
//   }
// })();
  // toggle size product config mobile


// bounce config image mobile after change option
(function () {
  const image = document.querySelector('.sticky_config_price .product__config_image--mobile img')
  const options = document.querySelectorAll('.product__info-wrapper [data-name="Rodzaj dywaników"] input')

  if (image) {
    options.forEach(el => {
      el.addEventListener('change', () => {
        image.classList.add('animate__bounce')
        setTimeout(() => {
          
          image.classList.remove('animate__bounce')
        }, 1000);
      })
    })   
  }
})();
// bounce config image mobile after change option



// product sticky price
(function () {
  const mainPriceElement = document.querySelector('.main_price');
  const stickyConfigPriceElement = document.querySelector('.sticky_container');

  if (stickyConfigPriceElement && window.innerWidth > 767) {
      // Создаем наблюдатель
      const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
          if (!entry.isIntersecting) {
              // Если элемент не виден, добавляем класс active
              stickyConfigPriceElement.classList.add('active');
          } else {
              // Если элемент виден, убираем класс active
              stickyConfigPriceElement.classList.remove('active');
          }
          });
      });
      
      window.addEventListener('load', () => {
          stickyConfigPriceElement.style.width = stickyConfigPriceElement.offsetWidth + 1 + 'px'
      })
      
      // Начинаем наблюдение за основным элементом
      if (mainPriceElement) {
          observer.observe(mainPriceElement);
      }
  } else if (stickyConfigPriceElement) {
    stickyConfigPriceElement.classList.add('active');
  }

})();
// product sticky price


// Click on the partial payment button in the product card
// document.addEventListener('DOMContentLoaded', function () {
//   const button = document.getElementById('partial-paymant__btn--add')

//   if (button) {
//     button.addEventListener('click', function () {

//       waitForElement('.product-form__submit', function () {
//         const productFormSubmitBtn = document.querySelector('.product-form__submit');
  
//         if (productFormSubmitBtn) {
//           productFormSubmitBtn.click();
//         }
//       });
//     });
//   }
  
// });
  
  function waitForElement(selector, callback) {
    const element = document.querySelector(selector);
  
    if (element) {
      callback();
    } else {
      requestAnimationFrame(function () {
        waitForElement(selector, callback);
      });
    }
  }


if (window.location.href.includes('tunel-eva-evamats')) {
  window.location.href = 'https://evamats.pl';
}
(function () {
  const addToCartButton = document.querySelector('.sticky_add_to_cart');

  if (addToCartButton) {
    addToCartButton.addEventListener('click', function () {

      const target = document.querySelector('#uwagi-do-zamowienia');
      if (target) {
          const rect = target.getBoundingClientRect();
          const offset = window.pageYOffset + rect.top - 300; // -300 пикселей вверх
          window.scrollTo({
              top: offset,
              behavior: 'smooth'
          });
      }

      document.querySelector('.product-form__submit').click()
    })    
  }
})(); 

// product tabs
(function () {
  document.addEventListener("DOMContentLoaded", function () {
    const tabs = document.querySelectorAll(".product_tabs__item");
    const containers = document.querySelectorAll(".product_tabs__containers_item");
    const descriptionSections = document.querySelectorAll(".image-with-text--description");

    function switchTab(activeId) {
        // Убираем активные классы
        tabs.forEach(tab => tab.classList.remove("active"));
        containers.forEach(container => container.classList.remove("active"));

        // Назначаем активный таб и контейнер
        const activeTab = document.querySelector(`.product_tabs__item[data-id="${activeId}"]`);
        const activeContainer = document.querySelector(`.product_tabs__containers_item[data-id="${activeId}"]`);

        if (activeTab) activeTab.classList.add("active");
        if (activeContainer) activeContainer.classList.add("active");

        // Показываем или скрываем .image-with-text--description
        descriptionSections.forEach(section => {
            if (activeId === "description") {
                section.style.display = "";
            } else {
                section.style.display = "none";
            }
        });
    }

    // Назначаем обработчики кликов
    tabs.forEach(tab => {
        tab.addEventListener("click", () => {
            const id = tab.getAttribute("data-id");
            switchTab(id);
        });
    });

    // Инициализация по умолчанию
    const firstTab = tabs[0];
    if (firstTab) {
        const id = firstTab.getAttribute("data-id");
        switchTab(id);
    }
  });
})();
// product tabs

Fancybox.bind("[data-fancybox]", {});